import { Component, Input, OnInit } from '@angular/core';
import { BehaviorSubject, Observable, combineLatest, of } from 'rxjs';
import { catchError, filter, switchMap, tap } from 'rxjs/operators';

import { DashboardRepository } from '../../dashboard.repository';
import { DateInterval } from '../../utils/bar-chart-tools';
import { KeyQuestionResult, KeyQuestionsStats } from '../key-questions-stats.interface';

@Component({
  selector: 'app-key-questions',
  templateUrl: './key-questions.component.html',
  styleUrls: ['./key-questions.component.scss'],
})
export class KeyQuestionsComponent implements OnInit {
  @Input() organizationIds$!: Observable<string[]>;

  @Input() dateInterval$ = new BehaviorSubject<DateInterval>({
    fromDate: new Date(),
    toDate: new Date(),
  });

  data: KeyQuestionsStats | null = null;

  bandLabels = ['pas du tout', 'pas souvent', 'de temps en temps', 'souvent', 'complètement'];

  constructor(private readonly dashboardRepository: DashboardRepository) {}

  ngOnInit() {
    combineLatest([this.organizationIds$, this.dateInterval$])
      .pipe(
        tap(([orgIds]) => {
          if (!orgIds?.length) {
            this.data = null;
          }
        }),
        filter(([orgIds]) => !!orgIds?.length),
        switchMap(([orgIds, interval]) =>
          this.dashboardRepository
            .getKeyQuestionsStats({
              organizationIds: orgIds,
              startDate: interval.fromDate.toISOString(),
              endDate: interval.toDate.toISOString(),
            })
            .pipe(
              catchError(() => {
                this.data = null;
                return of(null);
              }),
            ),
        ),
      )
      .subscribe((result) => {
        this.data = result;
      });
  }

  get percentageQuestions(): KeyQuestionResult[] {
    return (
      this.data?.questions?.filter((q) => q.displayType === 'percentage') ?? []
    );
  }

  get distributionQuestions(): KeyQuestionResult[] {
    return (
      this.data?.questions?.filter(
        (q) => q.displayType === 'distribution',
      ) ?? []
    );
  }

  get hasData(): boolean {
    return !!this.data && this.data.totalRespondents > 0;
  }

  getChartData$(question: KeyQuestionResult): Observable<any> {
    if (!question.distribution) {
      return of(null);
    }
    return of({
      distribution: question.distribution.map(band => ({
        count: band.count
      }))
    });
  }

  getLabels(question: KeyQuestionResult): string[] {
    return this.bandLabels;
  }

  getColors(question: KeyQuestionResult): string[] {
    return question.distribution?.map(band => band.color) || [];
  }
}
